import Vue from 'vue';
import * as XLSX from 'xlsx/xlsx.mjs';

import './day.plugin';

import Modal from '@/components/common/Modal';
import Check from '@/components/common/Check';
import Alert from '@/components/common/Alert';

import { useCommonStore } from '@/store';

const Common = {
  install(Vue, options) {
    // Mixin
    Vue.mixin({
      computed: {
        $count() {
          return num => {
            if(!num) {
              return 0;
            } else {
              const nums = num.toString().split('.');
              nums[0] = nums[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              return nums.join('.');
            }
          };
        },
        $imgSrc() {
          return imgPath => {
            if (!imgPath) {
              return;
            }
            return imgPath.startsWith('https') ? imgPath : process.env.CAMS_IMG_PATH + imgPath;
          };
        },
        $stringifyObj() {
          return obj => {
            return Object.keys(obj)
              .map(key => `${key}=${obj[key]}`)
              .join('&');
          };
        },
        $timezoneOffset() {
          return new Date().getTimezoneOffset() * 60000;
        },
        $assignObject() {
          return (obj1, obj2) => {
            if (!obj1 || !obj2) {
              return;
            }

            Object.keys(obj2)
              .filter(key => obj2[key] !== undefined && obj2[key] !== null)
              .forEach(key => {
                if (key === 'size' || key === 'page') {
                  obj1[key] = Number(obj2[key]);
                } else {
                  obj1[key] = obj2[key];
                }
              });
            return obj1;
          };
        },
        $cardNumber() {
          return cardNumber => {
            if (!cardNumber || cardNumber.length > 16) {
              return;
            }
            return cardNumber.match(/[0-9*]{1,4}/g)?.join('-') || cardNumber;
          };
        },
        $empty() {
          return value => {
            if (value === undefined || value === null || value === '') {
              return true;
            } else if (Array.isArray(value) && value.length === 0) {
              return true;
            } else if (typeof value === 'object' && Object.keys(value).length === 0) {
              return true;
            } else {
              return false;
            }
          };
        },
        $getHours() {
          return duration => {
            return this.$dayjs.duration({ seconds: duration }).asHours();
          };
        },
      },
      methods: {
        // Download Excel
        async $downloadExcel(data, namePrefix) {
          const xlsx = XLSX;

          // 엑셀 파일 생성
          const book = xlsx.utils.book_new();

          // sheet 생성 - json_to_sheet 방식
          const worksheetByJson = xlsx.utils.json_to_sheet(data);

          // 엑셀 파일에 sheet set(엑셀파일, 시트데이터, 시트명)
          xlsx.utils.book_append_sheet(book, worksheetByJson);

          // 엑셀 다운로드
          const date = this.$dayjs().format('YYYY-MM-DD');
          let filename = `${namePrefix || 'excel'}-${date}.xlsx`
          xlsx.writeFile(book, filename);
        },

        // Move Page
        $movePage(params) {
          if (params.query) {
            const query = {};
            Object.keys(params.query).forEach(key => {
              if (key === 'date') {
                query.since = this.$dayjs(params.query.date[0]).format('YYYY-MM-DDT00:00:00');
                query.until = this.$dayjs(params.query.date[1]).format('YYYY-MM-DDTHH:mm:ss');
              } else {
                query[key] = params.query[key];
              }
            });
            params.query = query;
          }

          if (!params.path) {
            this.$router.replace(params).catch(() => {});
            return;
          }
          this.$router.push(params);
        },

        // Error 처리
        $handleError(e) {
          this.$sentry.captureException(e);
          const errorStatus = e?.response?.status;
          switch (errorStatus) {
            case 401 : {
              useCommonStore().sessionInfo = null;
              this.$router.replace({path: '/error', query: {type: 'inactive'}}).catch(() => {});
              break;
            }
            case 404 :
            case 403 :
            case 400 : {
              this.$router.replace({path: '/error', query: {type: 'notfound'}}).catch(() => {});
              break;
            }
            case 408 : {
              this.$router.replace({path: '/error', query: {type: 'timeout'}}).catch(() => {});
              break;
            }
            case 500 :
            case 503 : {
              this.$router.replace({path: '/error', query: {type: 'service'}}).catch(() => {});
              break;
            }
            default :{
              this.$router.replace({path: '/error', query: {type: 'service'}}).catch(() => {});
            }
          }
        },
        $getParseQuery() {
          const url = window.location.search;
          const urlParams = new URLSearchParams(url);
          return Array.from(urlParams.keys()).reduce((acc, key) => {
            const value = key === 'sort' ? urlParams.getAll(key) : urlParams.get(key);
            switch (value) {
              case 'false': {
                acc[key] = false;
                break;
              }
              case 'true': {
                acc[key] = true;
                break;
              }
              case 'undefined': {
                acc[key] = undefined;
                break;
              }
              case 'null': {
                acc[key] = null;
                break;
              }
              default: {
                acc[key] = value;
              }
            }
            return acc;
          }, {});
        },
        $disabledDate(includeToday) {
          return date => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return includeToday ? today < date : today <= date;
          };
        },
      },
    });

    // prototype
    Vue.prototype.$modal = function (options, callback) {
      const $modal = {
        data: {
          callback,
        },
        init() {
          if (typeof options === 'string') {
            options = {
              text: options,
              onlyOk: true,
            };
          }
          this.show();
        },
        show() {
          const div = document.createElement('div');
          const nuxt = document.querySelector('#__nuxt');
          nuxt.appendChild(div);

          new Vue({
            i18n: this.i18n,
            data: this.data,
            render: h => h(Modal, { props: options }),
          }).$mount(div);
        },
      };
      $modal.i18n = this.$i18n;
      $modal.init();
    };

    Vue.prototype.$alert = function (options) {
      const $alert = {
        data: {
        },
        init() {
          this.show();
        },
        show() {
          const div = document.createElement('div');
          const nuxt = document.querySelector('#__nuxt');
          nuxt.appendChild(div);
          new Vue({
            data: this.data,
            render: h => h(Alert, { props: options }),
          }).$mount(div);
        },
      };
      $alert.init();
    };
  },
};
Vue.use(Common);

const components = { Check };

Object.entries(components).forEach(([name, component]) => {
  Vue.component(name, component);
});
